import type {
  MartiniDesktopBridge,
  DesktopBridgeErrorCode,
  DesktopMenuModel,
} from '../../../desktop/src/bridgeContract'
export type { DesktopAction, DesktopActivity } from '../../../desktop/src/bridgeContract'

const codes: readonly DesktopBridgeErrorCode[] = [
  'UNAVAILABLE',
  'INVALID_INPUT',
  'EXPIRED',
  'FILE_CHANGED',
  'IO',
  'NETWORK',
  'QUOTA',
]
export class DesktopBridgeError extends Error {
  constructor(readonly code: DesktopBridgeErrorCode) {
    super('Desktop operation failed')
    this.name = 'DesktopBridgeError'
  }
}
export function convertDesktopBridgeError(error: unknown): DesktopBridgeError {
  const code = error && typeof error === 'object' && 'code' in error ? error.code : undefined
  return new DesktopBridgeError(
    codes.includes(code as DesktopBridgeErrorCode) ? (code as DesktopBridgeErrorCode) : 'UNAVAILABLE',
  )
}
export function getDesktopBridge(minVersion = 1): MartiniDesktopBridge | null {
  if (typeof window === 'undefined' || !Number.isInteger(minVersion) || minVersion < 1) return null
  const value = (window as Window & { martiniDesktop?: unknown }).martiniDesktop
  if (!value || typeof value !== 'object') return null
  const candidate = value as MartiniDesktopBridge
  if (!Number.isInteger(candidate.bridgeVersion) || candidate.bridgeVersion < Math.max(1, minVersion)) return null
  if (
    !['ping', 'getAppInfo', 'reset', 'setActivity', 'onAction', 'setEnabledActions'].every(
      key => typeof (value as Record<string, unknown>)[key] === 'function',
    )
  )
    return null
  if (candidate.recordProjectOpen !== undefined && typeof candidate.recordProjectOpen !== 'function') return null
  return {
    ...(typeof candidate.recordProjectOpen === 'function'
      ? { recordProjectOpen: (project: { id: string; name: string }) => candidate.recordProjectOpen!(project) }
      : {}),
    ...(typeof candidate.onNavigate === 'function'
      ? { onNavigate: (listener: (projectId: string) => void) => candidate.onNavigate!(listener) }
      : {}),
    bridgeVersion: candidate.bridgeVersion,
    ...(candidate.bridgeVersion >= 2 &&
    candidate.assets &&
    ['prepare', 'startDrag', 'onDragged', 'reveal', 'clear'].every(
      key => typeof (candidate.assets as unknown as Record<string, unknown>)[key] === 'function',
    )
      ? { assets: candidate.assets }
      : {}),
    ping: async () => {
      try {
        const reply = await candidate.ping()
        if (!reply || reply.bridgeVersion !== candidate.bridgeVersion) throw new DesktopBridgeError('UNAVAILABLE')
        return reply
      } catch (error) {
        throw convertDesktopBridgeError(error)
      }
    },
    getAppInfo: async () => {
      try {
        const info = await candidate.getAppInfo()
        if (!info || typeof info.version !== 'string' || !['darwin', 'win32', 'linux'].includes(info.platform))
          throw new DesktopBridgeError('UNAVAILABLE')
        if (
          info.launchId !== undefined &&
          (typeof info.launchId !== 'string' || !/^[A-Za-z0-9_-]{8,64}$/.test(info.launchId))
        )
          throw new DesktopBridgeError('UNAVAILABLE')
        if (info.arch !== undefined && (typeof info.arch !== 'string' || !/^[A-Za-z0-9_-]{1,32}$/.test(info.arch)))
          throw new DesktopBridgeError('UNAVAILABLE')
        return info
      } catch (error) {
        throw convertDesktopBridgeError(error)
      }
    },
    reset: async () => {
      try {
        await candidate.reset()
      } catch (error) {
        throw convertDesktopBridgeError(error)
      }
    },
    // Menus are a capability, not a version: a v2 shell shipped before menus keeps the six-action path.
    ...(typeof candidate.setMenu === 'function'
      ? { setMenu: (model: DesktopMenuModel) => candidate.setMenu!(model) }
      : {}),
    setActivity: activity => candidate.setActivity(activity),
    ...(typeof candidate.setCompletedWhileAway === 'function'
      ? { setCompletedWhileAway: (count: number) => candidate.setCompletedWhileAway!(count) }
      : {}),
    setEnabledActions: actions => candidate.setEnabledActions(actions),
    onAction: listener => candidate.onAction(listener),
  }
}
